import { inject } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, from, map, catchError, of, switchMap, forkJoin } from 'rxjs';

import { LoggingService } from '../../logging/logging.service';
import { CacheStore, CacheStoreConfig, CacheEvent, CacheSource } from '../models/cache-models';
import { CacheEventBus } from '../services/cache-event-bus.service';
import { CacheRevalidationService } from '../services/cache-revalidation.service';

/**
 * Abstract base class for all cache stores.
 * Implements the CacheStore interface and provides common functionality.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class AbstractCacheStore<T extends {[key: string]: any}> implements CacheStore<T> {
  protected readonly logger = inject(LoggingService);
  protected readonly dbService = inject(NgxIndexedDBService);
  protected readonly eventBus = inject(CacheEventBus);
  protected readonly cacheRevalidationService = inject(CacheRevalidationService);

  /**
   * Constructor for AbstractCacheStore.
   *
   * @param config Configuration for the cache store.
   */
  constructor(protected readonly config: CacheStoreConfig) {}

  /**
   * Get an item from the cache by its ID.
   *
   * @param id ID of the item to retrieve.
   * @returns Observable of the item or null if not found.
   */
  public get(id: string | number): Observable<T | null> {
    return from(this.dbService.getByKey<T>(this.config.storeName, id)).pipe(
      map(item => {
        return item;
      }),
      catchError((error) => {
        this.logger.error(`Error getting item from cache: ${error}`);
        return of(null);
      })
    );
  }

  /**
   * Add or update an item in the cache.
   *
   * @param item The item to store.
   * @param source Source of the update (default: 'api').
   * @returns Observable of the stored item.
   */
  public set(item: T, source: CacheSource = 'api'): Observable<T> {
    return from(this.dbService.update<T>(this.config.storeName, item)).pipe(
      map(updatedItem => {
        // Extract project ID if configured
        let projectId: string | undefined;

        if (this.config.projectKeyPath && updatedItem[this.config.projectKeyPath]) {
          projectId = updatedItem[this.config.projectKeyPath];
        }

        // Create and dispatch update event
        const event: CacheEvent<T> = {
          type: 'update',
          storeId: this.config.storeName,
          itemId: updatedItem[this.config.keyPath],
          projectId,
          item: updatedItem,
          timestamp: Date.now(),
          source
        };

        this.eventBus.dispatch(event);

        return updatedItem;
      }),
      catchError((error) => {
        this.logger.error(`Error setting item in cache: ${error}`);
        throw error;
      })
    );
  }

  /**
   * Remove an item from the cache.
   *
   * @param id ID of the item to remove.
   * @param projectId Optional project ID for the item.
   * @param source Source of the removal (default: 'manual').
   * @returns Observable of boolean indicating success.
   */
  public remove(id: string | number, projectId?: string, source: CacheSource = 'manual'): Observable<boolean> {
    return from(this.dbService.deleteByKey(this.config.storeName, id)).pipe(
      map(() => {
        // Create and dispatch invalidation event
        const event: CacheEvent<T> = {
          type: 'invalidate',
          storeId: this.config.storeName,
          itemId: id,
          projectId,
          timestamp: Date.now(),
          source
        };

        this.eventBus.dispatch(event);
        return true;
      }),
      catchError((error) => {
        this.logger.error(`Error removing item from cache: ${error}`);
        return of(false);
      })
    );
  }

  /**
   * Clear all items from the cache or items for a specific project.
   *
   * @param projectId Optional project ID to scope the clear operation.
   * @param source Source of the clear operation (default: 'manual').
   *
   * @returns Observable of boolean indicating success.
   */
  public clear(projectId?: string, source: CacheSource = 'manual'): Observable<boolean> {
    if (projectId && this.config.projectKeyPath) {
      // Project-specific clear - only clear items for this project
      return this.getByIndex<T>(this.config.projectKeyPath, projectId).pipe(
        switchMap(items => {
          if (items.length === 0) {
            return of(true);
          }

          const ids = items.map((item: T) => item[this.config.keyPath]);

          return this.invalidateItems(ids, projectId, source);
        }),
        catchError(error => {
          this.logger.error(`Error clearing project-specific cache: ${error}`);
          return of(false);
        })
      );
    }

    // Clear entire store
    return from(this.dbService.clear(this.config.storeName)).pipe(
      map(() => true),
      catchError((error) => {
        this.logger.error(`Error clearing cache: ${error}`);
        return of(false);
      })
    );
  }

  /**
   * Get items by a specific index.
   *
   * @param indexName Name of the index to query.
   * @param key Key to look up in the index.
   * @returns Observable of items matching the index query.
   */
  public getByIndex<U = T>(indexName: string, key: string | number): Observable<U[]> {
    return from(this.dbService.getAllByIndex<U>(this.config.storeName, indexName, IDBKeyRange.only(key))).pipe(
      catchError((error) => {
        this.logger.error(`Error getting items by index: ${error}`);
        return of([]);
      })
    );
  }

  /**
   * Get an item by slug and project ID.
   * This is a default implementation that can be overridden by specific stores.
   *
   * @param slug The slug to search for.
   * @param projectId The project ID to scope the search.
   * @returns Observable of the item or null if not found.
   */
  public getBySlug(slug: string, projectId: string): Observable<T | null> {
    // Default implementation using index queries
    // Specific stores can override this for more efficient implementations
    return this.getByIndex('slug', slug).pipe(
      map(items => {
        if (!items || items.length === 0) {
          return null;
        }

        const projectKeyPath = this.config.projectKeyPath;

        // Filter by project ID if configured
        if (projectKeyPath) {
          const filteredItems = items.filter(item => item[projectKeyPath] === projectId);

          return filteredItems.length > 0 ? filteredItems[0] as T : null;
        }

        return items[0] as T;
      }),
      catchError((error) => {
        this.logger.error(`Error getting item by slug: ${error}`);
        return of(null);
      })
    );
  }

  /**
   * Check if an item needs to be refreshed by calling the changed-since API.
   * This method is used by the cacheOrFetch operator to determine if cached data is stale.
   *
   * @param itemId ID of the item to check.
   * @param projectId Project ID for scoping the request.
   * @returns Promise<boolean> indicating if the item needs refresh.
   */
  public async needsRefresh(itemId: string | number, projectId: string): Promise<boolean> {
    try {
      return await this.cacheRevalidationService.checkItemNeedsInvalidation(this.config.storeName, projectId, itemId);
    } catch (error) {
      this.logger.error(`Error checking if item ${itemId} needs refresh:`, error);
      // On error, assume item doesn't need refresh to avoid unnecessary API calls
      return false;
    }
  }

  /**
   * Invalidate multiple items at once.
   *
   * @param ids IDs of items to invalidate.
   * @param projectId Optional project ID for the items.
   * @param source Source of the invalidation.
   * @returns Observable of boolean indicating success.
   */
  private invalidateItems(ids: (string | number)[], projectId?: string, source: CacheSource = 'manual'): Observable<boolean> {
    if (!ids || ids.length === 0) {
      return of(true);
    }

    const deleteOperations = ids.map(id =>
      from(this.dbService.deleteByKey(this.config.storeName, id)).pipe(
        map(() => {
          // Create and dispatch invalidation event for each item
          const event: CacheEvent<T> = {
            type: 'invalidate',
            storeId: this.config.storeName,
            itemId: id,
            projectId,
            timestamp: Date.now(),
            source
          };

          this.eventBus.dispatch(event);
          return true;
        }),
        catchError(() => of(false))
      )
    );

    return forkJoin(deleteOperations).pipe(
      map(results => results.every(Boolean)),
      catchError(error => {
        this.logger.error('Error invalidating items:', error);

        return of(false);
      })
    );
  }
}
